import { ChangeDetectionStrategy, Component, Input, LOCALE_ID, OnInit, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  CsvColumnSpec,
  CsvSpec,
  DataContextBuilder,
  ElderChipListSelectComponent,
  ElderCsvExportBtnComponent,
  ElderDataToolbarComponent,
  ElderIntervalInputComponent,
  ElderIntervalPickerBindingDirective,
  ElderIntervalPickerToggleComponent,
  ElderNumberCellDirective,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  I18nPickAsyncPipe,
  IDataContext,
  Interval,
  IsoIntervalFormatUtil,
  SelectionModelPopupDirective,
} from '@elderbyte/ngx-starter'
import { Facility } from '../../../facilities/facility'
import { WorkCenter } from '../../../shopfloor/workcenters/work-center'
import { ControlTerminalMode } from '../../../terminals/control-terminal-mode'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { Instant, ZoneId } from '@js-joda/core'
import { ProductionListFilterBuilder } from '../production-list-filter-builder'
import { filter, map } from 'rxjs/operators'
import { Cell, CellType, Column, Row } from '../../../common/tables/table'
import { ProductionListService } from '../production-list.service'
import { ProductionListContext } from '../production-list-context'
import {
  ProductionListCombinedContext,
  ProductionListPreviewService,
} from '../production-list-preview.service'
import { ProductionListScope } from '../conf/prod-list-scope'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { DynamicPropertyCellComponent } from '../../../orders/work-items/columns/dynamic-property-cell/dynamic-property-cell.component'
import { DynamicPropertyHeaderComponent } from '../../../orders/work-items/columns/dynamic-property-header/dynamic-property-header.component'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { FormsModule } from '@angular/forms'
import { ProductionListConfSelectDirective } from '../conf/production-list-conf-select.directive'
import { WorkCenterBrowserComponent } from '../../../shopfloor/workcenters/work-center-browser/work-center-browser.component'
import { WorkCenterSelectDirective } from '../../../shopfloor/workcenters/work-center-select.directive'
import { MatFormField, MatSuffix } from '@angular/material/form-field'
import { AsyncPipe } from '@angular/common'
import { FacilityContextDirective } from '../../../context/facility-context/facility-context.directive'

@Component({
  selector: 'app-production-list',
  templateUrl: './production-list.component.html',
  styleUrls: ['./production-list.component.scss'],
  imports: [
    FacilityContextDirective,
    ElderTableComponent,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatFormField,
    ElderSelectComponent,
    WorkCenterSelectDirective,
    SelectionModelPopupDirective,
    WorkCenterBrowserComponent,
    ProductionListConfSelectDirective,
    ElderSelectValueDirective,
    ElderIntervalInputComponent,
    FormsModule,
    ElderChipListSelectComponent,
    ElderCsvExportBtnComponent,
    MatIconButton,
    MatIcon,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    ElderNumberCellDirective,
    DynamicPropertyHeaderComponent,
    MatCellDef,
    MatCell,
    MatSuffix,
    DynamicPropertyCellComponent,
    AsyncPipe,
    ElderTranslatePipe,
    I18nPickAsyncPipe,
    ElderIntervalPickerBindingDirective,
    ElderIntervalPickerToggleComponent,
  ],
})
export class ProductionListComponent implements OnInit {
  private _locale = inject(LOCALE_ID)
  private prodListPreviewService = inject(ProductionListPreviewService)

  /***************************************************************************
   *                                                                         *
   * Static                                                                  *
   *                                                                         *
   **************************************************************************/

  private static readonly AUTO_START_SPEC = {
    requiredFilters: [
      [
        ProductionListFilterBuilder.MODE,
        ProductionListFilterBuilder.WORK_CENTER_ID,
        ProductionListFilterBuilder.SCHEDULE_INTERVAL,
        ProductionListFilterBuilder.PROD_LIST_CONF,
      ],
      [
        ProductionListFilterBuilder.MODE,
        ProductionListFilterBuilder.WORK_CENTER_ID,
        ProductionListFilterBuilder.TIMESTAMP,
        ProductionListFilterBuilder.PROD_LIST_CONF,
      ],
    ],
  }

  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _mode$ = new BehaviorSubject<ControlTerminalMode>(ControlTerminalMode.PREVIEW)
  private readonly _workCenter$ = new BehaviorSubject<WorkCenter>(null)
  private readonly _scheduleInterval$ = new BehaviorSubject<Interval>(null)
  // todo live mode will probably need to be refreshed every now and then.
  private readonly _timestamp$ = new BehaviorSubject<Instant>(Instant.now())
  private readonly _prodListConfId$ = new BehaviorSubject<string>(null)
  private readonly _scopes$ = new BehaviorSubject<ProductionListScope[]>([])

  private _enabledDisplayModes: ControlTerminalMode[] = [
    ControlTerminalMode.LIVE,
    ControlTerminalMode.PREVIEW,
  ]

  private readonly _columns$ = new BehaviorSubject<Column[]>([])

  public readonly data: IDataContext<Row>
  public readonly ProductionListDisplayMode = ControlTerminalMode

  public csvColumnSpecs: CsvColumnSpec<Row>[]
  public csvSpec: CsvSpec<Row> = new CsvSpec<Row>()

  public facilityId: string
  public hideIntervals: boolean
  public hideWorkCenterSelect: boolean

  public ctx: ProductionListContext

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const productionListService = inject(ProductionListService)

    this._prodListConfId$.subscribe((confId) => {
      if (confId) {
        productionListService.getColumns(confId).subscribe({
          next: (cols) => this._columns$.next(cols),
          error: (err) => this.log.error(err),
        })
      }
    })

    this.data = DataContextBuilder.start<Row>()
      .autoStartOn(ProductionListComponent.AUTO_START_SPEC)
      .build(productionListService)

    combineLatest([
      this._mode$,
      this._scheduleInterval$,
      this._workCenter$,
      this._timestamp$,
      this._prodListConfId$,
      this._columns$,
    ])
      .pipe(
        filter(
          ([mode, scheduleInterval, workCenter, timestamp, prodListConfId, columns]) =>
            this.isValidContext(mode, workCenter, scheduleInterval, timestamp, prodListConfId) &&
            columns?.length > 0
        ),
        map(([mode, scheduleInterval, workCenter, timestamp, prodListConfId]) =>
          this.buildContext(mode, workCenter, prodListConfId, scheduleInterval, timestamp)
        ),
        filter((context) => !context.equals(this.ctx))
      )
      .subscribe((context) => {
        this.ctx = context

        this.data.filter.updateFilters(this.buildFilters(context))

        this.csvSpec = this.buildCsvSpec(this.createCsvFilename(context))
      })
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.scheduleInterval) {
      this.scheduleInterval = Interval.today(ZoneId.systemDefault())
    }

    if (this.enabledDisplayModes.length > 0) {
      this.mode = this.enabledDisplayModes[0]
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set scheduleIntervalInput(schedule: Interval) {
    if (schedule) {
      this.hideIntervals = true
      this.scheduleInterval = schedule
    }
  }

  @Input()
  public set workCenterInput(workCenter: WorkCenter) {
    if (workCenter) {
      this.hideWorkCenterSelect = true
      this.workCenter = workCenter
    }
  }

  public set facility(facility: Facility) {
    if (facility) {
      this.facilityId = facility.id
    }
  }

  public get workCenter(): WorkCenter {
    return this._workCenter$.getValue()
  }

  public set workCenter(workCenter: WorkCenter) {
    if (!!workCenter && workCenter !== this._workCenter$.getValue()) {
      this._workCenter$.next(workCenter)
    }
  }

  public get scheduleInterval(): Interval {
    return this._scheduleInterval$.getValue()
  }

  public set scheduleInterval(interval: Interval) {
    this._scheduleInterval$.next(interval)
  }

  @Input()
  public set enabledDisplayModes(modes: ControlTerminalMode[]) {
    if (modes.length > 0) {
      this._enabledDisplayModes = modes
    }
    this.mode = this._enabledDisplayModes[0]
  }

  public get enabledDisplayModes(): ControlTerminalMode[] {
    return this._enabledDisplayModes
  }

  public get mode$(): Observable<ControlTerminalMode> {
    return this._mode$.asObservable()
  }

  public get mode(): ControlTerminalMode {
    return this._mode$.getValue()
  }

  public set mode(mode: ControlTerminalMode) {
    this._mode$.next(mode)
  }

  public get modeSelection(): ControlTerminalMode[] {
    return [this.mode]
  }

  public set modeSelection(selection: ControlTerminalMode[]) {
    if (selection.length > 0) {
      this.mode = selection[0]
    }
  }

  public get prodListConfId$(): Observable<string> {
    return this._prodListConfId$.asObservable()
  }

  public get prodListConfId(): string {
    return this._prodListConfId$.getValue()
  }

  @Input()
  public set prodListConfId(id: string) {
    this._prodListConfId$.next(id)
  }

  public get columns(): Column[] {
    return this._columns$.getValue()
  }

  public get columns$(): Observable<Column[]> {
    return this._columns$.asObservable()
  }

  public get timestamp(): Instant {
    return this._timestamp$.getValue()
  }

  @Input()
  public set scopes(scopes: ProductionListScope[]) {
    this._scopes$.next(scopes)
  }

  public get scopes$(): Observable<ProductionListScope[]> {
    return this._scopes$.asObservable()
  }

  public get scopes(): ProductionListScope[] {
    return this._scopes$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onPreviewProdList(_: Event): void {
    this.prodListPreviewService.previewProdList(
      new ProductionListCombinedContext(
        this.workCenter?.id,
        this.scheduleInterval,
        this.timestamp,
        this.mode,
        this.scopes,
        null,
        this.prodListConfId
      )
    )
  }

  public onClick($event: any) {
    // TODO for debugging purposes
    this.log.debug('Entry clicked:', $event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildContext(
    mode: ControlTerminalMode,
    workCenter: WorkCenter,
    prodListConfId: string,
    scheduleInterval?: Interval,
    timestamp?: Instant
  ): ProductionListContext {
    switch (mode) {
      case ControlTerminalMode.LIVE:
        return ProductionListContext.live(workCenter, timestamp, prodListConfId)
      case ControlTerminalMode.PREVIEW:
        return ProductionListContext.preview(workCenter, scheduleInterval, prodListConfId)
    }
  }

  private isValidContext(
    mode: ControlTerminalMode,
    workCenter: WorkCenter,
    scheduleInterval?: Interval,
    timestamp?: Instant,
    prodListConfId?: string
  ): boolean {
    switch (mode) {
      case ControlTerminalMode.PREVIEW:
        return !!workCenter && !!scheduleInterval && !!prodListConfId
      case ControlTerminalMode.LIVE:
        return !!workCenter && !!timestamp && !!prodListConfId
    }
  }

  private buildFilters(context: ProductionListContext) {
    return ProductionListFilterBuilder.start()
      .mode(context.mode)
      .workCenterId(context.workCenter?.id)
      .scheduleInterval(context.scheduleInterval)
      .timestamp(context.timestamp)
      .prodListConfId(context.prodListConfId)
      .build()
  }

  private buildCsvSpec(filename: string): CsvSpec<Row> {
    this.csvColumnSpecs = this.createCsvSpecs()
    return CsvSpec.start<Row>(filename).withColumnSpecs(...this.csvColumnSpecs)
  }

  private createCsvFilename(context: ProductionListContext): string {
    const fileName = `Produktionsliste ${context.workCenter.name}`
    const validInterval = !!context?.scheduleInterval?.start && !!context?.scheduleInterval?.end
    if (context.mode === ControlTerminalMode.PREVIEW && validInterval) {
      const formattedInterval = IsoIntervalFormatUtil.formatIsoInterval(
        context.scheduleInterval,
        ZoneId.SYSTEM,
        this._locale
      )
      return `${fileName} ${formattedInterval}`
    } else {
      return fileName
    }
  }

  private createCsvSpecs(): CsvColumnSpec<Row>[] {
    const csvColumns = []
    for (let i = 0; i < this.columns?.length; i++) {
      csvColumns.push(this.createCsvSpec(i))
    }
    return csvColumns
  }

  private createCsvSpec(index: number): CsvColumnSpec<Row> {
    return CsvColumnSpec.start<Row>()
      .withValueResolverFn((r) => this.returnContentIfPossible(r.cells[index]))
      .withColumnHeaderText(this.columns[index].name ?? '')
  }

  private returnContentIfPossible(cell: Cell): string {
    return cell.type === CellType.TEXT ? cell.content : ''
  }
}
